/**
 * RoomCreationForm renders the UI for creating a new room.
 *
 * Why: Users need a form to enter room code and PIN when creating a room.
 * This is a pure component that receives all data as props.
 *
 * When to use: Rendered by RoomCreationFormContainer. Use directly in
 * Storybook stories with mock props.
 */
import { Button } from '@merels/ui'
import { useTheme } from '../../../contexts/ThemeContext'

interface RoomCreationFormProps {
  roomName: string
  error: string
  onRoomNameChange: (name: string) => void
  onCreate: () => void
  onCancel: () => void
}

export function RoomCreationForm({
  roomName,
  error,
  onRoomNameChange,
  onCreate,
  onCancel
}: RoomCreationFormProps) {
  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()

  const inputStyle = {
    borderColor: appColorShades.main,
    '--tw-ring-color': appColorShades.main,
  } as React.CSSProperties & { '--tw-ring-color': string }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = appColorShades.main
    e.target.style.boxShadow = `0 0 0 2px ${appColorShades.main}40`
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = appColorShades.main
    e.target.style.boxShadow = 'none'
  }

  return (
    <div className="max-w-md w-full text-center">
      <div className="flex justify-center mb-4">
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          width="120"
          height="120"
        >
          {/* square frame */}
          <path
            d="M15 15 V85 H85 V15 Z"
            stroke={appColorShades.main}
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />

          {/* three filled corner nodes */}
          <g fill={appColorShades.main}>
            <circle cx="15" cy="15" r="8" />
            <circle cx="15" cy="85" r="8" />
            <circle cx="85" cy="85" r="8" />
          </g>

          {/* large circle: between previous two placements */}
          {/* center (75,25) is inside the square but closer to the top-right */}
          <circle
            cx="75"
            cy="25"
            r="21"
            stroke={appColorShades.main}
            strokeWidth="8"
            fill={appColorShades.main}
          />
        </svg>
      </div>
      <h1
        className="text-6xl font-semibold mb-6 leading-none"
        style={{
          color: appColorShades.main,
          fontFamily: "'MorrisRomanBlack', serif",
        }}
      >
        Merels
      </h1>
      <h2 className="text-base font-bold mb-4 text-primary">Create New Room</h2>
      {error && (
        <div className="bg-danger-bg/20 border-2 border-danger-bg/40 text-danger p-4 rounded-lg mb-4 font-bold">
          <strong>Error:</strong> {error}
        </div>
      )}
      <div className="flex flex-col gap-4 items-center">
        <input
          type="text"
          placeholder="Room Name"
          value={roomName}
          onChange={(e) => onRoomNameChange(e.target.value)}
          className="w-full max-w-xs p-3 border-2 rounded-lg text-base bg-surface text-primary focus:outline-none focus:ring-2 transition-colors"
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus
        />
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button onClick={onCreate}>
            Create Room
          </Button>
        </div>
      </div>
    </div>
  )
}
